// Typ: FindSectorAreaFromAngleTask
// Temat: Okrąg i koło → Elementy, długości i pola → Pole wycinka koła
// Wzór:  P = (α/360°) · π r^2

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import PiInput, { parsePiLikeInput, formatPiValueLatex } from "../../../../components/inputs/PiInput";

const DEC_TOL = 0.1;

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// k·π zapisany jako ułamek mieszany (jeśli potrzeba)
function formatMixedTimesPi(n, d) {
  const q = Math.floor(n / d);
  const r = n % d;
  if (r === 0) return q === 1 ? "\\pi" : `${q}\\,\\pi`;
  if (q === 0)  return `\\dfrac{${r}}{${d}}\\,\\pi`;
  return `${q}\\,\\dfrac{${r}}{${d}}\\,\\pi`;
}

// ===== generator =====
export function generateTask() {
  const r = Math.floor(Math.random() * 9) + 2; // 2..10

  // kąty co 15° + wielokrotności 40°; unikalne i bez 360°
  const ANGLES_15 = Array.from({ length: 24 }, (_, i) => 15 * (i + 1));   // 15..360
  const ANGLES_40 = Array.from({ length: 9  }, (_, i) => 40 * (i + 1));   // 40..360
  const ALL_ANGLES = [...new Set([...ANGLES_15, ...ANGLES_40])].filter(a => a !== 360);

  const alpha = ALL_ANGLES[Math.floor(Math.random() * ALL_ANGLES.length)];
  return { type: "FindSectorAreaFromAngleTask", data: { r, alpha } };
}

// ===== render task =====
export function renderTask({ data }) {
  const { r, alpha } = data;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      Dany jest okrąg o promieniu <InlineMath math={`r=${r}`} /> oraz
      kąt środkowy <InlineMath math={`\\alpha=${alpha}^{\\circ}`} />.
      Oblicz pole wycinka <InlineMath math={"P"} />.
    </div>
  );
}

// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <PiInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={"P"}
      placeholder="np. 25π, 9/2 π, 1 1/3 π lub 78.5 (±0.1)"
    />
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const { r, alpha } = data;
  const expected = (alpha / 360) * Math.PI * r * r;

  const parsed =
    typeof userAnswer === "object" && userAnswer?.parsed != null
      ? userAnswer.parsed
      : parsePiLikeInput(typeof userAnswer === "object" ? userAnswer.raw : userAnswer);

  if (parsed == null || !Number.isFinite(parsed)) return false;
  return Math.abs(parsed - expected) <= DEC_TOL;
}

// ===== exact answer (mieszany ułamek razy π) =====
export function renderAnswer({ data }) {
  const { r, alpha } = data;
  const num0 = alpha * r * r; // α·r^2
  const den0 = 360;
  const g = gcd(num0, den0);
  const n = num0 / g, d = den0 / g;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`P = ${formatMixedTimesPi(n, d)}`} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { r, alpha } = data;
  const approx = (((alpha / 360) * Math.PI * r * r)).toFixed(2);

  const num0 = alpha * r * r;
  const g = gcd(num0, 360);
  const n = num0 / g, d = 360 / g;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Wzór na pole wycinka:</div>
      <BlockMath math={'P = \\dfrac{\\alpha}{360^{\\circ}}\\cdot \\pi r^{2}'} />

      <div><strong>Krok 2.</strong> Podstawiamy <InlineMath math={`r=${r}`} /> i <InlineMath math={`\\alpha=${alpha}^{\\circ}`} />:</div>
      <BlockMath math={`P = \\dfrac{${alpha}}{360}\\cdot \\pi \\cdot ${r}^{2} = \\dfrac{${alpha}\\cdot ${r}^{2}}{360}\\,\\pi`} />

      <div><strong>Krok 3.</strong> Uproszczenie:</div>
      <BlockMath math={`P = ${formatMixedTimesPi(n, d)}`} />

      <div><strong>Krok 4.</strong> (Opcjonalnie) Przybliżenie:</div>
      <BlockMath math={`P \\approx ${approx}`} />
    </div>
  );
}

// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`P = ${formatPiValueLatex(raw) || ""}`} />
    </div>
  );
}
